import React, { useState, useEffect, useRef } from 'react';
import { AlertCircle, Loader2, Lock, Mail, Smartphone } from 'lucide-react';
import { loginOtp, sendOtpEmail, sendOtpMobile } from 'services/bidder';
import useAuth from './useAuth';

const Alert = ({ children, type = 'info' }: { children: React.ReactNode; type?: 'info' | 'error' }) => (
  <div className={`flex items-center p-4 mb-4 text-sm rounded-lg ${
    type === 'error' ? 'text-red-800 bg-red-100' : 'text-blue-800 bg-blue-100'
  }`}>
    <AlertCircle className="flex-shrink-0 inline w-5 h-5 mr-3" />
    <div>{children}</div>
  </div>
);

const Button = ({ children, disabled, variant = 'primary', ...props }: React.ButtonHTMLAttributes<HTMLButtonElement> & { children: React.ReactNode; variant?: 'primary' | 'secondary' }) => (
  <button
    className={`w-full px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-opacity-50 ${
      disabled
        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
        : variant === 'primary'
        ? 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500'
        : 'bg-gray-200 text-gray-800 hover:bg-gray-300 focus:ring-gray-400'
    }`}
    disabled={disabled}
    {...props}
  >
    {disabled && variant === 'primary' ? (
      <Loader2 className="inline-block w-5 h-5 mr-2 animate-spin" />
    ) : null}
    {children}
  </button>
);

const OtpInput = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (index: number, inputValue: string) => {
    const newValue = value.split('');
    newValue[index] = inputValue;
    onChange(newValue.join(''));

    if (inputValue && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  return (
    <div className="flex justify-between">
      {[0, 1, 2, 3, 4, 5].map((index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="text"
          maxLength={1}
          value={value[index] || ''}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          className="w-12 h-12 text-center text-2xl border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-blue-500"
        />
      ))}
    </div>
  );
};

const TwoFactorAuth = ({ onValidOTP, children }: { onValidOTP: (token: string) => void; children: React.ReactNode }) => {
  const [otp, setOtp] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const [otpMethod, setOtpMethod] = useState<'email' | 'sms'>('email');

  const { token } = useAuth();

  const email = token?.data?.attributes.find((attr: any) => attr.type === 'email')?.value;
  const mobile = token?.data?.attributes.find((attr: any) => attr.type === 'mobile_number')?.value;

  useEffect(() => {
    sendOtp('email');
  }, []);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setInterval(() => setCountdown(prev => prev - 1), 1000);
    }
    return () => clearInterval(timer);
  }, [countdown]);

  async function sendOtp(method: 'email' | 'sms') {
    setOtpMethod(method);
    setCountdown(30);
    setError('');
    setOtp(''); // Clear the OTP input when switching methods

    try {
      if (method === 'email' && email) {
        await sendOtpEmail({ value: email });
      } else if (method === 'sms' && mobile) {
        await sendOtpMobile({ value: mobile });
      }
    } catch (err) {
      setError('Failed to send OTP. Please try again.');
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length !== 6) {
      setError('Please enter a 6-digit OTP.');
      return;
    }
    setIsValidating(true);
    setError('');

    try {
      const response: any = await loginOtp({
        type: otpMethod === 'email' ? 'email' : 'mobile_number',
        value: otpMethod === 'email' ? email : mobile,
        otp: otp
      });

      if (response.access_token) {
        setIsAuthenticated(true);
        onValidOTP(response.access_token);
      } else {
        throw new Error('No access token received');
      }
    } catch (err) {
      setError('Incorrect OTP. Please try again.');
    } finally {
      setIsValidating(false);
    }
  };

  const renderOtpDestination = () => {
    if (otpMethod === 'email') {
      return `${email?.slice(0, 3)}...${email?.slice(email.indexOf('@'))}`;
    } else if (otpMethod === 'sms') {
      return `${mobile?.slice(0, 2)}1 xxxxxx${mobile?.slice(-4)}`;
    }
    return '';
  };

  if (isAuthenticated) {
    return <>{children}</>;
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100">
      <div className="w-full max-w-md p-8 space-y-8 bg-white rounded-2xl shadow-lg">
        <div className="text-center">
          <div className="inline-block p-3 rounded-full bg-blue-100">
            <Lock className="w-8 h-8 text-blue-600" />
          </div>
          <h2 className="mt-4 text-2xl font-extrabold text-gray-900">Two-Factor Authentication</h2>
          <p className="mt-2 text-sm text-gray-600">Verify your identity to proceed</p>
        </div>

        <form onSubmit={handleSubmit} className="mt-8 space-y-6">
          <div className="text-center">
            <p className="text-sm text-gray-600">
              OTP sent to: {renderOtpDestination()}
            </p>
          </div>
          <div>
            <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-2">
              Enter OTP
            </label>
            <OtpInput value={otp} onChange={setOtp} />
          </div>
          {error && <Alert type="error">{error}</Alert>}
          <Button type="submit" disabled={isValidating}>
            {isValidating ? 'Validating...' : 'Verify OTP'}
          </Button>
          <div className="text-center text-sm text-gray-600 space-y-2">
            {countdown > 0 ? (
              <p>Resend OTP in {countdown} seconds</p>
            ) : (
              <button
                onClick={() => sendOtp(otpMethod)}
                className="text-blue-600 hover:text-blue-800 focus:outline-none"
              >
                Resend OTP
              </button>
            )}
            {mobile && (
              <button
                onClick={() => sendOtp(otpMethod === 'email' ? 'sms' : 'email')}
                className="text-blue-600 hover:text-blue-800 focus:outline-none flex items-center justify-center w-full"
                disabled={countdown > 0}
              >
                {otpMethod === 'email' ? (
                  <>
                    <Smartphone className="w-4 h-4 mr-2" />
                    Switch to SMS OTP
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Switch to Email OTP
                  </>
                )}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default TwoFactorAuth;